import { FormikProps } from "formik";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { IArticle } from "../../../../../../@types/Articles";
import { Close } from "../../../../../../assets/icons/Close";
import { RichTextEditor } from "../../../../../../components/RichTextEditor";
import {
  IOption,
  SelectDropdown,
} from "../../../../../../components/SelectDropdown";
import { SkeletonLoading } from "../../../../../../components/Skeleton";
import { TextField } from "../../../../../../components/TextField";
import { useModal } from "../../../../../../hooks/useModal";
import { useWardLabel } from "../../../../../../hooks/useWardLabel";
import { mixpanelActions } from "../../../../../../utils/mixpanel";
import { AddLinkModal } from "./components/AddLinkModal";
import {
  CardImageSelector,
  ContentContainer,
  ContentRow,
  ContentSubRow,
  ErrorMessage,
  inputCss,
  MainContainer,
  RicheTextEditorContainer,
  SelectedImageContainer,
  selectCss,
  AddLinkText,
  URLPreviewContainer,
  CloseContainer,
  URLLink,
} from "./ContentSection.styles";

interface IContentSectionProps {
  formik: FormikProps<IArticle>;
  wardOptions: IOption[];
  tagOptions: IOption[];
  openImageLibrary: () => void;
}

export function ContentSection({
  formik,
  wardOptions,
  tagOptions,
  openImageLibrary,
}: IContentSectionProps) {
  const { t } = useTranslation();

  const { openModal, closeModal } = useModal();
  const wardLabel = useWardLabel();

  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);

    return () => {
      setIsMounted(false);
    };
  }, [formik]);

  const tagsDropdown = !isMounted ? null : (
    <SelectDropdown
      placeholder={t("careInfo_content_selectTags")}
      className={selectCss()}
      multiple
      width={216}
      height={42}
      value={formik.values.tags}
      onValueChange={(values) => {
        formik.setFieldValue("tags", values as string[]);
      }}
      options={tagOptions}
      onFocus={() => mixpanelActions.track("UserAtcion: Tags")}
    />
  );

  const wardsDropdown = !isMounted ? null : (
    <SelectDropdown
      placeholder={t("careInfo_content_selectWardsPlaceholder", {
        wards: wardLabel.pluralLower,
      })}
      className={selectCss()}
      multiple
      width={216}
      height={42}
      value={formik.values.wards?.map((ward) => {
        return ward.toString();
      })}
      onValueChange={(values) => {
        formik.setFieldValue("wards", values as string[]);
      }}
      options={wardOptions}
    />
  );

  const handleRemoveImage = () => {
    formik.setFieldValue("imageName", undefined);
  };

  const shouldShowErrorMessage = (
    field: "title" | "wards" | "imageName" | "articleText"
  ) => {
    return formik.touched[field] ? formik.errors[field] ?? "" : "";
  };

  const handleAddLinkOnClose = (url?: string) => {
    if (url) {
      formik.setFieldValue("articleUrl", url);
      formik.setFieldValue("articleText", "");
    }

    closeModal();
  };

  const handleAddLink = () => {
    mixpanelActions.track("User Action: AddURLInsteadButton");

    openModal(<AddLinkModal onClose={handleAddLinkOnClose} />, {
      width: "100%",
      height: "534px",
      showCloseButton: true,
    });
  };

  const handleRemoveLink = () => {
    formik.setFieldValue("articleUrl", undefined);
  };

  return (
    <MainContainer>
      <ContentContainer>
        <ContentRow>
          <p>{t("careInfo_content_titleLabel")} *</p>
          <TextField
            label=""
            name="title"
            value={formik.values.title}
            placeholder={t("careInfo_content_titlePlaceholder")}
            onChange={formik.handleChange}
            backgroudColor="white"
            className={inputCss()}
            maxLength={50}
            errorMessage={shouldShowErrorMessage("title")}
            onFocus={() => mixpanelActions.track("UserAction: Title")}
          />
        </ContentRow>
        <ContentRow>
          <p>{t("careInfo_content_cardDescription")}</p>
          <TextField
            label=""
            name="description"
            placeholder={t("careInfo_content_cardDescriptionPlaceholder")}
            value={formik.values.description}
            onChange={formik.handleChange}
            backgroudColor="white"
            className={inputCss()}
            maxLength={90}
            onFocus={() => mixpanelActions.track("UserAction: Description")}
          />
        </ContentRow>
        <ContentRow isSmallInput>
          <ContentSubRow isError={shouldShowErrorMessage("wards").length > 0}>
            <p>{wardLabel.plural} *</p>
            {wardOptions ? (
              wardsDropdown
            ) : (
              <SkeletonLoading width={216} height={42} />
            )}
            <ErrorMessage>{shouldShowErrorMessage("wards")}</ErrorMessage>
          </ContentSubRow>
          <ContentSubRow>
            <p>{t("careInfo_content_tags")}</p>
            {tagOptions ? (
              tagsDropdown
            ) : (
              <SkeletonLoading width={216} height={42} />
            )}
          </ContentSubRow>
        </ContentRow>
        <ContentRow>
          <p>{t("careInfo_content_image")}</p>
          {formik.values.imageName === undefined ? (
            <CardImageSelector
              onClick={() => {
                mixpanelActions.track("UserAction: ChooseImage");
                openImageLibrary();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  mixpanelActions.track("UserAction: ChooseImage");
                  openImageLibrary();
                }
              }}
              tabIndex={0}
            >
              {t("careInfo_content_chooseCardImage")}
            </CardImageSelector>
          ) : (
            <CardImageSelector>
              <SelectedImageContainer>
                <p>{formik.values.imageName}</p>
                <div
                  aria-label={t("careInfo_content_imageAlt")}
                  style={{ marginLeft: "8px" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveImage();
                  }}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    handleRemoveImage();
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <Close />
                </div>
              </SelectedImageContainer>
            </CardImageSelector>
          )}
          <ErrorMessage>{shouldShowErrorMessage("imageName")}</ErrorMessage>
        </ContentRow>
      </ContentContainer>

      {formik.values.articleUrl === undefined ? (
        <RicheTextEditorContainer
          isError={shouldShowErrorMessage("articleText").length > 1}
        >
          <AddLinkText onClick={() => handleAddLink()}>
            {t("careInfo_content_addLinkInstead")}
          </AddLinkText>
          <RichTextEditor
            placeholderTxt={t("careInfo_content_textPlaceholder")}
            height={415}
            value={formik.values.articleText}
            onValueChange={(value) => {
              if (value === "<p><br></p>") {
                formik.setFieldValue("articleText", "");
                return;
              }
              formik.setFieldValue("articleText", value);
            }}
            onFocus={() => mixpanelActions.track("UserAction: Text")}
          />
          <ErrorMessage isArticleText>
            {shouldShowErrorMessage("articleText")}
          </ErrorMessage>
        </RicheTextEditorContainer>
      ) : (
        <URLPreviewContainer>
          <URLLink
            onClick={() => window.open(formik.values.articleUrl, "_blank")}
          >
            {formik.values.articleUrl}
          </URLLink>
          <CloseContainer onClick={handleRemoveLink}>
            <Close />
          </CloseContainer>
        </URLPreviewContainer>
      )}
    </MainContainer>
  );
}
